import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, getRoleBadgeClass, getRoleLabel, getStatusBadgeClass } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Search, RefreshCw, Plus, Pencil, UserRound, Lock, Trash2, Power, PowerOff, RotateCcw } from 'lucide-react';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';

const EMPTY_FORM = {
  full_name: '',
  email: '',
  password: '',
  confirm_password: '',
  phone: '',
  job_title: '',
  role: 'tratador',
  base_ids: [],
  status: 'active',
  internal_notes: '',
  permissions: [],
};

const PERMISSION_LABELS = {
  'daily_records.view': 'Acompanhamento - Ver',
  'daily_records.create': 'Acompanhamento - Criar',
  'daily_records.edit': 'Acompanhamento - Editar histórico',
  'daily_records.delete': 'Acompanhamento - Excluir histórico',
  'daily_records.attach': 'Acompanhamento - Anexar fotos/vídeos',
  'support_tickets.view': 'Suporte - Ver chamados',
  'support_tickets.create': 'Suporte - Abrir chamado',
  'support_tickets.manage': 'Suporte - Atender chamados',
};

const formatPermissionLabel = (permission) => {
  if (PERMISSION_LABELS[permission]) return PERMISSION_LABELS[permission];
  const [group, action] = permission.split('.');
  const groupLabel = (group || permission)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
  const actionLabel = (action || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return action ? `${groupLabel} - ${actionLabel}` : groupLabel;
};

export default function UsersPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [bases, setBases] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editablePermissions, setEditablePermissions] = useState([]);
  const [programmerOnlyPermissions, setProgrammerOnlyPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterBase, setFilterBase] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  const canCreateUsers = hasPermission(user, 'users.create');
  const canEditUsers = hasPermission(user, 'users.edit');
  const canToggleStatus = hasPermission(user, 'users.status');
  const canResetPassword = hasPermission(user, 'users.password.reset');
  const canDeleteUsers = hasPermission(user, 'users.delete');
  const canEditUserPermissions = hasPermission(user, 'users.permissions.edit');

  useEffect(() => {
    fetchBootstrap();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [search, filterRole, filterBase, filterStatus]);

  const fetchBootstrap = async () => {
    setLoading(true);
    try {
      const [basesResponse, rolesResponse] = await Promise.all([
        axios.get(`${API}/bases`),
        axios.get(`${API}/users/roles-permissions`),
      ]);
      setBases(basesResponse.data || []);
      setRoles(rolesResponse.data?.roles || []);
      setEditablePermissions(rolesResponse.data?.editable_permissions || []);
      setProgrammerOnlyPermissions(rolesResponse.data?.programmer_only_permissions || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar configurações de usuários');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (filterRole !== 'all') params.append('role', filterRole);
      if (filterBase !== 'all') params.append('base_id', filterBase);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const response = await axios.get(`${API}/users?${params.toString()}`);
      setUsers(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = useMemo(() => roles.map((item) => ({ value: item.value, label: item.label })), [roles]);
  const roleDefaultsMap = useMemo(
    () => Object.fromEntries(roles.map((item) => [item.value, item.permissions || []])),
    [roles],
  );

  const getRoleDefaultPermissions = (role) => roleDefaultsMap[role] || [];

  const openCreateDialog = () => {
    setEditingUser(null);
    setForm({
      ...EMPTY_FORM,
      role: 'tratador',
      base_ids: user?.base_ids || [],
      permissions: getRoleDefaultPermissions('tratador'),
    });
    setShowFormDialog(true);
  };

  const openEditDialog = (targetUser) => {
    setEditingUser(targetUser);
    setForm({
      full_name: targetUser.full_name || targetUser.name || '',
      email: targetUser.email || '',
      password: '',
      confirm_password: '',
      phone: targetUser.phone || '',
      job_title: targetUser.job_title || '',
      role: targetUser.role || 'tratador',
      base_ids: targetUser.base_ids || [],
      status: targetUser.status || 'active',
      internal_notes: targetUser.internal_notes || '',
      permissions: targetUser.permissions || getRoleDefaultPermissions(targetUser.role || 'tratador'),
    });
    setShowFormDialog(true);
  };

  const openViewDialog = (targetUser) => {
    setSelectedUser(targetUser);
    setShowViewDialog(true);
  };

  const toggleBaseSelection = (baseId, checked) => {
    setForm((current) => ({
      ...current,
      base_ids: checked
        ? [...current.base_ids, baseId]
        : current.base_ids.filter((value) => value !== baseId),
    }));
  };

  const togglePermissionSelection = (permission, checked) => {
    setForm((current) => ({
      ...current,
      permissions: checked
        ? [...current.permissions, permission].sort()
        : current.permissions.filter((value) => value !== permission),
    }));
  };

  const handleRoleChange = (value) => {
    setForm((current) => ({
      ...current,
      role: value,
      permissions: getRoleDefaultPermissions(value),
    }));
  };

  const restoreDefaultPermissions = () => {
    setForm((current) => ({
      ...current,
      permissions: getRoleDefaultPermissions(current.role),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error('Nome completo e email são obrigatórios');
      return;
    }
    if (!editingUser && (!form.password || !form.confirm_password)) {
      toast.error('Senha e confirmação são obrigatórias');
      return;
    }
    if (!editingUser && form.password !== form.confirm_password) {
      toast.error('A confirmação de senha não confere');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const payload = {
          full_name: form.full_name.trim(),
          phone: form.phone || null,
          job_title: form.job_title || null,
          role: form.role,
          base_ids: form.base_ids,
          status: form.status,
          internal_notes: form.internal_notes || null,
        };
        if (canEditUserPermissions) {
          payload.permissions = form.permissions;
        }
        await axios.put(`${API}/users/${editingUser.id}`, payload);
        toast.success('Usuário atualizado com sucesso');
      } else {
        await axios.post(`${API}/users`, {
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          confirm_password: form.confirm_password,
          phone: form.phone || null,
          job_title: form.job_title || null,
          role: form.role,
          base_ids: form.base_ids,
          status: form.status,
          internal_notes: form.internal_notes || null,
          permissions: canEditUserPermissions ? form.permissions : undefined,
        });
        toast.success('Usuário criado com sucesso');
      }
      setShowFormDialog(false);
      setForm(EMPTY_FORM);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (targetUser) => {
    const nextStatus = targetUser.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/users/${targetUser.id}/status`, { status: nextStatus });
      toast.success(nextStatus === 'active' ? 'Usuário reativado' : 'Usuário desativado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    if (!resetPassword || resetPassword !== resetPasswordConfirm) {
      toast.error('A nova senha e a confirmação precisam ser iguais');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/users/${selectedUser.id}/reset-password`, {
        new_password: resetPassword,
        confirm_password: resetPasswordConfirm,
      });
      toast.success('Senha redefinida com sucesso');
      setShowResetDialog(false);
      setResetPassword('');
      setResetPasswordConfirm('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success('Usuário excluído com sucesso');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <header className="bg-white shadow-sm border-b border-emerald-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="hidden md:flex items-center justify-between gap-6">
            <div className="flex items-center gap-3 min-w-0">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img src="/bw-symbol-final.png" alt="Instituto BW" className="h-12 w-12 object-contain" />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="text-xl font-bold text-emerald-800 truncate">Gestão de Usuários</h1>
                <p className="text-xs text-emerald-500 truncate">Cadastro, permissões, status e segurança</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchUsers}><RefreshCw className="w-4 h-4 mr-2" />Atualizar</Button>
              <Button variant="outline" onClick={() => navigate('/')}>Voltar</Button>
              <Button variant="ghost" className="text-red-600" onClick={logout}>Sair</Button>
            </div>
          </div>

          <div className="md:hidden space-y-4">
            <div className="flex items-start gap-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img src="/bw-symbol-final.png" alt="Instituto BW" className="h-11 w-11 object-contain" />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="text-lg font-bold text-emerald-800 break-words">Gestão de Usuários</h1>
                <p className="text-xs text-emerald-500 break-words">Cadastro, permissões, status e segurança</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" onClick={fetchUsers} className="w-full"><RefreshCw className="w-4 h-4" /></Button>
              <Button variant="outline" onClick={() => navigate('/')} className="w-full">Voltar</Button>
              <Button variant="ghost" className="w-full text-red-600" onClick={logout}>Sair</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <Card>
          <CardContent className="pt-6 grid grid-cols-1 lg:grid-cols-[1.2fr_repeat(3,minmax(0,1fr))_auto] gap-4">
            <div>
              <Label>Busca por nome ou email</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" placeholder="Ex: maria@cras.com" />
              </div>
            </div>
            <div>
              <Label>Perfil</Label>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {roleOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Base</Label>
              <Select value={filterBase} onValueChange={setFilterBase}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {bases.map((base) => (
                    <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Ativos</SelectItem>
                  <SelectItem value="inactive">Inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              {canCreateUsers && (
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={openCreateDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Usuário
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="hidden md:block">
          <Card>
            <CardHeader>
              <CardTitle>Usuários</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="py-10 text-center text-gray-500">Carregando usuários...</div>
              ) : users.length === 0 ? (
                <div className="py-10 text-center text-gray-500">Nenhum usuário encontrado.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-left text-gray-500">
                      <tr>
                        <th className="py-3 pr-4">Usuário</th>
                        <th className="py-3 pr-4">Perfil</th>
                        <th className="py-3 pr-4">Bases</th>
                        <th className="py-3 pr-4">Status</th>
                        <th className="py-3 pr-4">Criado em</th>
                        <th className="py-3 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((item) => (
                        <tr key={item.id} className="border-b last:border-0">
                          <td className="py-3 pr-4">
                            <div className="font-medium text-emerald-900">{item.full_name || item.name}</div>
                            <div className="text-xs text-gray-500">{item.email}</div>
                          </td>
                          <td className="py-3 pr-4">
                            <Badge className={getRoleBadgeClass(item.role)}>{getRoleLabel(item.role)}</Badge>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex flex-wrap gap-1">
                              {(item.base_names || []).length > 0 ? (item.base_names || []).map((baseName) => (
                                <Badge key={`${item.id}-${baseName}`} variant="outline">{baseName}</Badge>
                              )) : <span className="text-gray-400">Sem base</span>}
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <Badge className={getStatusBadgeClass(item.status)}>{item.status === 'active' ? 'Ativo' : 'Inativo'}</Badge>
                          </td>
                          <td className="py-3 pr-4 text-gray-500">
                            {item.created_at ? new Date(item.created_at).toLocaleString('pt-BR') : '-'}
                          </td>
                          <td className="py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => openViewDialog(item)}>Visualizar</Button>
                              {canEditUsers && <Button variant="outline" size="sm" onClick={() => openEditDialog(item)}><Pencil className="w-4 h-4 mr-1" />Editar</Button>}
                              {canToggleStatus && <Button variant="outline" size="sm" onClick={() => handleToggleStatus(item)}>{item.status === 'active' ? <PowerOff className="w-4 h-4 mr-1" /> : <Power className="w-4 h-4 mr-1" />}{item.status === 'active' ? 'Desativar' : 'Reativar'}</Button>}
                              {canResetPassword && <Button variant="outline" size="sm" onClick={() => { setSelectedUser(item); setShowResetDialog(true); }}><Lock className="w-4 h-4 mr-1" />Redefinir Senha</Button>}
                              {canDeleteUsers && <Button variant="outline" size="sm" className="text-red-600" onClick={() => { setSelectedUser(item); setShowDeleteDialog(true); }}><Trash2 className="w-4 h-4 mr-1" />Excluir</Button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="md:hidden space-y-4">
          {loading ? (
            <Card><CardContent className="py-10 text-center text-gray-500">Carregando usuários...</CardContent></Card>
          ) : users.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-gray-500">Nenhum usuário encontrado.</CardContent></Card>
          ) : users.map((item) => (
            <Card key={item.id}>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-emerald-900">{item.full_name || item.name}</p>
                    <p className="text-sm text-gray-500 break-all">{item.email}</p>
                  </div>
                  <Badge className={getStatusBadgeClass(item.status)}>{item.status === 'active' ? 'Ativo' : 'Inativo'}</Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge className={getRoleBadgeClass(item.role)}>{getRoleLabel(item.role)}</Badge>
                  {(item.base_names || []).map((baseName) => <Badge key={`${item.id}-mobile-${baseName}`} variant="outline">{baseName}</Badge>)}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => openViewDialog(item)}>Visualizar</Button>
                  {canEditUsers && <Button variant="outline" onClick={() => openEditDialog(item)}>Editar</Button>}
                  {canToggleStatus && <Button variant="outline" onClick={() => handleToggleStatus(item)}>{item.status === 'active' ? 'Desativar' : 'Reativar'}</Button>}
                  {canResetPassword && <Button variant="outline" onClick={() => { setSelectedUser(item); setShowResetDialog(true); }}>Redefinir Senha</Button>}
                  {canDeleteUsers && <Button variant="outline" className="text-red-600 col-span-2" onClick={() => { setSelectedUser(item); setShowDeleteDialog(true); }}>Excluir</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do usuário</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-3 text-sm">
              <p><strong>Nome:</strong> {selectedUser.full_name || selectedUser.name}</p>
              <p><strong>Email:</strong> {selectedUser.email}</p>
              <p><strong>Perfil:</strong> {getRoleLabel(selectedUser.role)}</p>
              <p><strong>Telefone:</strong> {selectedUser.phone || '-'}</p>
              <p><strong>Cargo:</strong> {selectedUser.job_title || '-'}</p>
              <p><strong>Status:</strong> {selectedUser.status === 'active' ? 'Ativo' : 'Inativo'}</p>
              <div>
                <strong>Permissões:</strong>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(selectedUser.permissions || []).length > 0 ? (selectedUser.permissions || []).map((permission) => (
                    <Badge key={`${selectedUser.id}-permission-${permission}`} variant="outline">{formatPermissionLabel(permission)}</Badge>
                  )) : <span className="text-gray-500">Sem permissões definidas</span>}
                </div>
              </div>
              <div>
                <strong>Bases:</strong>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(selectedUser.base_names || []).length > 0 ? (selectedUser.base_names || []).map((baseName) => (
                    <Badge key={`${selectedUser.id}-view-${baseName}`} variant="outline">{baseName}</Badge>
                  )) : <span className="text-gray-500">Sem base vinculada</span>}
                </div>
              </div>
              <p><strong>Observações:</strong> {selectedUser.internal_notes || '-'}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showFormDialog} onOpenChange={setShowFormDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Atualize os dados permitidos do usuário.' : 'Cadastre um novo usuário no sistema.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nome completo</Label>
                <Input value={form.full_name} onChange={(event) => setForm((current) => ({ ...current, full_name: event.target.value }))} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} disabled={Boolean(editingUser)} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
              </div>
              {!editingUser && (
                <>
                  <div>
                    <Label>Senha</Label>
                    <Input type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} />
                  </div>
                  <div>
                    <Label>Confirmar senha</Label>
                    <Input type="password" value={form.confirm_password} onChange={(event) => setForm((current) => ({ ...current, confirm_password: event.target.value }))} />
                  </div>
                </>
              )}
              <div>
                <Label>Telefone</Label>
                <Input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} />
              </div>
              <div>
                <Label>Cargo / função</Label>
                <Input value={form.job_title} onChange={(event) => setForm((current) => ({ ...current, job_title: event.target.value }))} />
              </div>
              <div>
                <Label>Perfil</Label>
                <Select value={form.role} onValueChange={handleRoleChange} disabled={!canCreateUsers && editingUser}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm((current) => ({ ...current, status: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Bases vinculadas</Label>
              <div className="mt-2 grid md:grid-cols-2 gap-3 rounded-lg border p-4 max-h-56 overflow-y-auto">
                {bases.map((base) => (
                  <label key={base.id} className="flex items-center gap-3 text-sm">
                    <Checkbox checked={form.base_ids.includes(base.id)} onCheckedChange={(checked) => toggleBaseSelection(base.id, Boolean(checked))} />
                    <span>{base.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Observações internas</Label>
              <Textarea value={form.internal_notes} onChange={(event) => setForm((current) => ({ ...current, internal_notes: event.target.value }))} rows={4} />
            </div>

            {canEditUserPermissions && (
              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <Label>Permissões do usuário</Label>
                    <p className="text-sm text-gray-500">
                      O perfil define o padrão inicial. Aqui é possível liberar ou remover acessos específicos.
                    </p>
                  </div>
                  <Button type="button" variant="outline" onClick={restoreDefaultPermissions}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Restaurar padrão do perfil
                  </Button>
                </div>
                <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-800">
                  Perfil atual: <strong>{getRoleLabel(form.role)}</strong>. Permissões padrão: {getRoleDefaultPermissions(form.role).length}.
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {editablePermissions.map((permission) => (
                    <label key={permission} className="flex items-start gap-3 rounded-md border p-3 text-sm">
                      <Checkbox
                        checked={form.permissions.includes(permission)}
                        onCheckedChange={(checked) => togglePermissionSelection(permission, Boolean(checked))}
                      />
                      <span className="space-y-1">
                        <span className="block font-medium text-gray-900">{formatPermissionLabel(permission)}</span>
                        <span className="block text-xs text-gray-500">{permission}</span>
                        {programmerOnlyPermissions.includes(permission) && (
                          <span className="block text-xs font-medium text-rose-600">Exclusiva do programador</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowFormDialog(false)}>Cancelar</Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
                {saving ? 'Salvando...' : editingUser ? 'Salvar alterações' : 'Criar usuário'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Redefinir senha</DialogTitle>
            <DialogDescription>Defina uma nova senha para {selectedUser?.full_name || selectedUser?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nova senha</Label>
              <Input type="password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} />
            </div>
            <div>
              <Label>Confirmar nova senha</Label>
              <Input type="password" value={resetPasswordConfirm} onChange={(event) => setResetPasswordConfirm(event.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowResetDialog(false)}>Cancelar</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={handleResetPassword} disabled={saving}>Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir usuário</DialogTitle>
            <DialogDescription>
              A exclusão física só é permitida quando não houver vínculos críticos. Na maioria dos casos, use a desativação.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
            <Button variant="outline" className="flex-1 text-red-600" onClick={handleDelete} disabled={saving}>Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
